import { toSafeDiagnostic } from '@/lib/errors/safe-error-kind'
import { logger } from '@/lib/logger'
import { useAuthStore } from '@/lib/stores/auth'

type Flag = { current: boolean }

/**
 * 公開の画面から入って後回しにしたセッションの確認（ICD-99）を1回走らせる。
 * auth-provider.tsx の中に置かないのは、max-lines（400行）に収めるため。
 *
 * 🔴 `check`（auth-provider の initAuth）は通信の失敗を中で握るが、try の外（控えを戻す setUser の
 *    保存＝localStorage の容量不足など）で投げると reject する。そのときは「確かめられなかった」として
 *    読み込み中を解き、ログインが無ければ未確認の印を立てて /login へ送らない（initAuth の catch・
 *    GF-D-08 と同じ扱い）。`.catch` を外すと読み込み中のまま止まり、例外も取りこぼす
 *    （tests/quality/promise-finally-without-catch-ratchet.test.ts が止める形）。
 * 🔴 状態を戻してから記録する。記録（logger.error）が万一投げても読み込み中のまま止めない。
 *    記録そのものの失敗は握りつぶさず reject として上へ出す（ブラウザでは Sentry の
 *    unhandledrejection の受け口が拾う）。
 *
 * @param check 確認の本体（initAuth）
 * @param flags.mountedRef 画面に付いている間だけ true（離れたあとは状態を書かない）
 * @param flags.inFlightRef 確認の最中は true（リダイレクトの effect が結果を待たずに送らない印）
 * @param flags.onSettled 終わったら呼ぶ（画面に付いているときだけ・リダイレクトの effect に結果を見直させる）
 */
export function startDeferredSessionCheck(
  check: () => Promise<void>,
  flags: { mountedRef: Flag; inFlightRef: Flag; onSettled: () => void }
): Promise<void> {
  flags.inFlightRef.current = true
  return check()
    .catch((error: unknown) => {
      if (flags.mountedRef.current) settleAsUnconfirmed()
      // 例外の本文は出さない（氏名などが入り得る）。種類名と要約だけの例外に作り直して送る
      logger.error('[auth] 後回しにしたセッションの確認が例外で終わりました（ICD-99）', {
        error: toSafeDiagnostic(error),
      })
    })
    .finally(() => {
      flags.inFlightRef.current = false
      if (flags.mountedRef.current) flags.onSettled()
    })
}

/** 確かめられなかったときの状態を入れる（読み込み中を解く・ログインが無ければ未確認の印）。 */
function settleAsUnconfirmed(): void {
  // 1回の setState で入れる: 保存が投げても値は先に入る（zustand persist）ので、2回に分けると
  // 1回目で止まり読み込み中が残る
  const { isAuthenticated } = useAuthStore.getState()
  try {
    useAuthStore.setState(
      isAuthenticated ? { isLoading: false } : { sessionCheckErrored: true, isLoading: false }
    )
  } catch (persistError: unknown) {
    // 容量不足が続くとここでも保存が投げる。画面の値は入っているので、保存の失敗だけを記録して終える
    // （投げ直すと呼び出し元の void で未処理の reject になる）
    logger.error('[auth] 確認の結果を画面に入れたあとの保存に失敗しました（ICD-99）', {
      error: toSafeDiagnostic(persistError),
    })
  }
}
